import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { showAlert } from "store/reducers/alertReducer";
import { updatererralcode, getRewardsList } from "store/actions/couponAction";
import CustomLabel from "components/common/CustomLabel";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import FormButtons from "components/common/FormButtons";
import NumberField from "components/common/NumberField";
import SingleSelect from "components/common/SingleSelect";
import Spinners from "components/common/Spinners";
import { referralValidationSchema } from "components/common/Validation";
import { monthOptions } from "components/constants/DropdownOptions";
import { setPageTitle } from "components/common/Utils";

const initialValues = {
  earnedLeafForReceiver: "",
  earnedLeafForSender: "",
  expiredIn: "",
};

const Referral = ({ mode }) => {
  const { t } = useTranslation();
  document.title = mode === "add" ?   setPageTitle("Referral Code" ,t) : setPageTitle("Update Reward" ,t); 
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const { isLoading, loading, rewardsList } = useSelector(
    (state) => state.Coupons
  );

  const [formValues, setFormValues] = useState(initialValues);

  useEffect(() => {
    dispatch(getRewardsList());
  }, [dispatch]);

  useEffect(() => {
    if (rewardsList) {
      const { earnedLeafForReceiver, earnedLeafForSender, expiredIn } =
        rewardsList?.referralInfo || {};

      setFormValues({
        earnedLeafForReceiver: earnedLeafForReceiver || "",
        earnedLeafForSender: earnedLeafForSender || "",
        expiredIn: expiredIn || "",
      });
    }
  }, [rewardsList]);

  const handleOnSubmit = (values, { setSubmitting }) => {
    const { earnedLeafForReceiver, earnedLeafForSender, expiredIn } = values;

    const data = {
      id: rewardsList?._id,
      referralInfo: {
        earnedLeafForReceiver: Number(earnedLeafForReceiver),
        earnedLeafForSender: Number(earnedLeafForSender),
        expiredIn: Number(expiredIn),
      },
    };

    dispatch(updatererralcode(data))
      .then((action) => {
        const { statusCode, success, message } = action?.payload || {};
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
        } else {
          dispatch(
            showAlert({ text: message || "Update failed", type: "error" })
          );
        }
      })
      .catch((err) => {
        dispatch(showAlert({ text: "An error occurred", type: "error" }));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="page-content common_page add_workshop_page">
      {loading ? (
        <Spinners />
      ) : (
        <Container fluid>
          <Row>
            <DetailsPageTitle title={t("Referral Code")} />
            <Formik
              initialValues={formValues}
              enableReinitialize={true}
              validationSchema={referralValidationSchema}
              onSubmit={handleOnSubmit}
            >
              {({ values, setFieldValue, isSubmitting, errors, touched }) => (
                <Form>
                  <Row className="common_wrapper">
                    <Col lg={2}>
                      <div className="form_field">
                        <CustomLabel label={t("Sender Leaves")} required />
                        <NumberField
                          name="earnedLeafForSender"
                          value={values.earnedLeafForSender}
                          placeholder={t("")}
                          onChange={({ value }) => {
                            setFieldValue("earnedLeafForSender", value);
                          }}
                          min={1}
                          hasError={
                            touched.earnedLeafForSender &&
                            errors.earnedLeafForSender
                          }
                        />
                        {touched.earnedLeafForSender &&
                          errors.earnedLeafForSender && (
                            <div className="text-danger small mt-1">
                              {t(errors.earnedLeafForSender)}
                            </div>
                          )}
                      </div>
                    </Col>
                    <Col lg={2}>
                      <div className="form_field">
                        <CustomLabel label={t("Receiver Leaves")} required />
                        <NumberField
                          name="earnedLeafForReceiver"
                          value={values.earnedLeafForReceiver}
                          placeholder={t("")}
                          onChange={({ value }) => {
                            setFieldValue("earnedLeafForReceiver", value);
                          }}
                          min={1}
                          hasError={
                            touched.earnedLeafForReceiver &&
                            errors.earnedLeafForReceiver
                          }
                        />
                        {touched.earnedLeafForReceiver &&
                          errors.earnedLeafForReceiver && (
                            <div className="text-danger small mt-1">
                              {t(errors.earnedLeafForReceiver)}
                            </div>
                          )}
                      </div>
                    </Col>
                    <Col lg={4}>
                      <div className="form_field">
                        <CustomLabel label={t("Expiration Period")} required />
                        <SingleSelect
                          name="expiredIn"
                          value={values.expiredIn}
                          options={monthOptions}
                          placeholder={t("Select months")}
                          handleSingleSelectChange={(e) => {
                            setFieldValue("expiredIn", e.value);
                          }}
                          hasError={touched.expiredIn && errors.expiredIn}
                        />
                        {touched.expiredIn && errors.expiredIn && (
                          <div className="text-danger  mt-1">
                            {t(errors.expiredIn)}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row className="px-0 my-4">
                    <FormButtons
                      isLoading={isLoading || isSubmitting}
                      addBtnText={t("Update")}
                    />
                  </Row>
                </Form>
              )}
            </Formik>
          </Row>
        </Container>
      )}
    </div>
  );
};

export default Referral;
